import React, { useEffect, useId, useRef, useState } from 'react';
import { createPopper } from '@popperjs/core';

function ToolPill({ tool }) {
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const popperInstanceRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !triggerRef.current || !tooltipRef.current) {
      return undefined;
    }

    popperInstanceRef.current = createPopper(triggerRef.current, tooltipRef.current, {
      placement: 'top',
      modifiers: [
        { name: 'offset', options: { offset: [0, 10] } },
        { name: 'preventOverflow', options: { padding: 10 } }
      ]
    });

    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy();
        popperInstanceRef.current = null;
      }
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        className="ui-chip flex w-full items-center gap-2 rounded-xl border border-ink/10 bg-snow px-2 py-2 text-left"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onClick={() => setIsOpen((current) => !current)}
        aria-describedby={tooltipId}
      >
        <img src={tool.logo} alt={`${tool.name} logo`} className="h-4 w-4 object-contain" loading="lazy" />
        <span className="text-[11px] font-medium text-ink/82">{tool.name}</span>
      </button>

      <div
        ref={tooltipRef}
        id={tooltipId}
        role="tooltip"
        className={`z-50 max-w-[190px] rounded-lg border border-ink/20 bg-coal px-2 py-1.5 text-[10px] leading-relaxed text-white shadow-ambient transition-opacity duration-150 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        {tool.use || `${tool.name} powers this part of the service.`}
      </div>
    </div>
  );
}

function ServiceFlipCard({
  title,
  customerView,
  description,
  points,
  tags,
  tools,
  imageUrl,
  mobileExpanded,
  onToggle
}) {
  const detailsId = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-details`;

  return (
    <article className={`service-flip ${mobileExpanded ? 'is-flipped' : ''}`}>
      <div className="service-flip-inner">
        <div className="service-face rounded-[1.65rem] border border-ink/12 bg-snow p-6 md:p-7">
          <img
            src={imageUrl}
            alt=""
            className="absolute inset-0 h-full w-full rounded-[1.65rem] object-cover grayscale opacity-40"
            aria-hidden="true"
          />
          <div className="service-overlay absolute inset-0 rounded-[1.65rem]" aria-hidden="true" />

          <div className="relative flex h-full flex-col">
            <div className="flex items-center justify-between">
              <p className="meta-label">Service</p>
              <span className="accent-dot" />
            </div>

            <h3 className="mt-4 font-display text-3xl font-bold uppercase leading-[0.96] tracking-[-0.02em] md:text-[2.15rem]">
              {title}
            </h3>

            <p className="mt-3 text-sm leading-relaxed text-ink/82">{customerView}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="ui-chip rounded-full border border-accent/50 bg-accent/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-auto pt-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink/55">Tap / Hover for details</p>
            </div>
          </div>
        </div>

        <div
          id={detailsId}
          className="service-face service-back rounded-[1.65rem] border border-ink/12 bg-white p-6 md:p-7"
        >
          <div className="flex h-full flex-col">
            <p className="meta-label">What You Get</p>
            <h3 className="mt-3 font-display text-2xl font-bold uppercase tracking-[-0.02em] md:text-[1.95rem]">{title}</h3>
            <p className="copy-refined mt-3 text-sm text-ink/80">{description}</p>

            <ul className="copy-refined mt-5 space-y-2 text-xs text-ink/74">
              {points.map((point) => (
                <li key={point} className="flex items-start gap-2">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-5">
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/55">Languages & Tools</p>
              <div className="grid grid-cols-2 gap-2">
                {tools.map((tool) => (
                  <ToolPill key={tool.name} tool={tool} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="ui-chip mt-4 inline-flex rounded-full border border-ink/20 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink md:hidden"
        onClick={onToggle}
        aria-expanded={mobileExpanded}
        aria-controls={detailsId}
      >
        {mobileExpanded ? 'Show Front' : 'Show Details'}
      </button>
    </article>
  );
}

export default ServiceFlipCard;
